import { AgGridReact } from 'ag-grid-react'
import { useContext, useRef } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { localeText } from '../../../../actions/datagrid.js'
import 'ag-grid-enterprise'
import { useQuery } from '@tanstack/react-query'
import { finagoTimeEventMappingsQuery } from '../../../../api/index.js'
import Chip from '../../../../common/chip/Chip.jsx'
import { capitalizeFirstLetter } from '../../../../common/utils.jsx'
import {
	absenceNS,
	translatedCausecode,
} from '../../../../mgmtview/personalprofile/newabscencemodal/newabscencemodal.constants.js'
import { useGetFinagoTimeEventTypesOptions } from '../SettingsProcountor/hooks/useGetFinagoTimeEventTypesOptions.js'
import { useGetFormsOfSalaryOptions } from '../SettingsProcountor/hooks/useGetFormsOfSalaryOptions.ts'
import { SettingsTimeContext } from './SettingsTimeContext.jsx'
import { getPaidness, parseFinagoEventTypeId } from './Utils.js'

const defaultColDef = {
	resizable: true,
	sortable: true,
	editable: false,
	flex: 1,
}

export const FinagoTimeEventMapping = () => {
	const { t, i18n } = useTranslation()
	const { t: tAbsence } = useTranslation(absenceNS)
	const gridRef = useRef(null)
	const context = useContext(SettingsTimeContext) || {}
	const { editedCells = [], setEditedCells = () => {} } = context

	const { data: mappings, isLoading: mappingsLoading, isError: mappingsError } = useQuery(finagoTimeEventMappingsQuery)
	const {
		options: eventTypeOptions,
		isLoading: typesLoading,
		isError: typesError,
	} = useGetFinagoTimeEventTypesOptions()
	const formsOfSalaryOptions = useGetFormsOfSalaryOptions()

	const editCells = (cell) => {
		const changed = [...editedCells]
		const idx = changed.findIndex(({ id }) => id === cell.id)
		if (idx > -1) {
			changed[idx] = cell
		} else {
			changed.push(cell)
		}
		setEditedCells(changed)
	}

	const paidnessRenderer = (params) => {
		const paidness = getPaidness(params.data?.eventType)
		const unpaidVariant = paidness === 'Unpaid' ? 'secondary--outlined' : 'primary--outlined'
		return <Chip variant={paidness === 'Paid' ? 'secondary' : unpaidVariant}>{capitalizeFirstLetter(t(paidness))}</Chip>
	}

	const eventTypeRenderer = (params) => {
		const match = eventTypeOptions.find((o) => String(o.value) === String(params.value))
		return <span>{match ? match.label : (params.value ?? '')}</span>
	}

	const statusRenderer = (params) => {
		if (params.value === null || params.value === undefined) return ''
		return <Chip variant={params.value ? 'success' : 'default'}>{params.value ? t('Active') : t('Inactive')}</Chip>
	}

	const columnDefs = [
		{
			headerName: t('EventType', 'Tapahtumatyyppi'),
			field: 'eventType',
			editable: false,
			valueFormatter: (params) => (params.value ? translatedCausecode(params.value, tAbsence) : ''),
			filter: 'agTextColumnFilter',
		},
		{
			headerName: t('Paidness'),
			field: 'paidness',
			editable: false,
			valueGetter: (params) => getPaidness(params.data?.eventType),
			cellRenderer: paidnessRenderer,
			filter: 'agSetColumnFilter',
			filterParams: {
				values: ['Paid', 'Unpaid', 'Other'],
				cellRenderer: (params) => capitalizeFirstLetter(t(params.value)),
			},
		},
		{
			headerName: t('TimeEventType', 'Time-tapahtuma'),
			field: 'finagoEventTypeId',
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: { values: eventTypeOptions.map((o) => o.value) },
			valueParser: (params) => parseFinagoEventTypeId(params.newValue),
			valueFormatter: (params) => {
				const match = eventTypeOptions.find((o) => String(o.value) === String(params.value))
				return match ? match.label : (params.value ?? '')
			},
			cellRenderer: eventTypeRenderer,
			filter: 'agSetColumnFilter',
		},
		{
			headerName: t('FormOfSalary'),
			field: 'formOfSalary',
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: formsOfSalaryOptions.map((o) => o.value),
			},
			valueFormatter: (params) => {
				if (params.value == null) return ''
				const match = formsOfSalaryOptions.find((o) => String(o.value) === String(params.value))
				return match ? match.label : String(params.value)
			},
			filter: 'agSetColumnFilter',
			hide: true,
		},
		{
			headerName: t('PayGroup'),
			field: 'paymentGroup',
			editable: true,
			filter: 'agTextColumnFilter',
			hide: true,
		},
		{
			headerName: t('Status'),
			field: 'active',
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: { values: [true, false] },
			valueFormatter: (params) => (params.value === true ? t('Active') : params.value === false ? t('Inactive') : ''),
			cellRenderer: statusRenderer,
			filter: 'agSetColumnFilter',
		},
	]

	if (mappingsLoading || typesLoading) {
		return <Alert variant="info">{t('Loading', 'Ladataan')}...</Alert>
	}

	if (mappingsError || typesError) {
		return (
			<Alert variant="danger">
				{t('FinagoTimeEventTypesLoadError', 'Finago Time tapahtumia ei voitu ladata. Yritä uudelleen.')}
			</Alert>
		)
	}

	return (
		<div id="finago-time-event-mapping-table">
			<Alert className="w-auto d-inline-block">
				{t('benefitMapping.editInfo', 'To edit fields, simply click on them')}
			</Alert>
			<div className="ag-theme-material ag-event-mapping styles-ag mb-3" style={{ height: '500px', marginRight: '0' }}>
				<AgGridReact
					ref={gridRef}
					defaultColDef={defaultColDef}
					columnDefs={columnDefs}
					rowData={mappings}
					getRowId={(params) => String(params.data.id)}
					localeText={localeText(i18n)}
					singleClickEdit
					onCellValueChanged={(event) => editCells(event.data)}
					defaultExcelExportParams={{ exportAsExcelTable: true }}
					defaultCsvExportParams={{ columnSeparator: ';' }}
				/>
			</div>
		</div>
	)
}
